import { Intro, DataTable, Image, PopOverMenu, Pill } from 'components/commons';
import { useApi, useFilter, useMount } from 'hooks';
import useModal from 'hooks/useModal';
import { DefaultProfile } from 'images';
import React, { useCallback, useMemo } from 'react';
import { formatAmount, formatName, prettifyCustomerStatus } from 'utils';
import { savingsColumns } from './savings-columns';
import CustomerFilter from './savings-filter';
import { savingsFilterState } from './savings-filter.state';

import styles from './savings.module.scss';
import locale from 'localization';
import CustomerStatus from 'enums/driver-status';
import { getCustomerAccounts } from 'apis/customer.api';

import { generateReport } from 'apis/generate-report.api';

const SavingsModule = () => {
  const detailModal = useModal();

  const { modifyFilter, modifyFilters, filterState, requestState, submitFilter } = useFilter(savingsFilterState());

  const {
    request: getCustomnerAccountRequest,
    loading: fetchingCustomer,
    result: getCustomerResult = { customerAccounts: [], count: 0 }
  } = useApi({
    api: getCustomerAccounts,
    pageError: true,
    params: {
      productId: 1
    }
  });

  useMount(() => {
    fetchCustomers(requestState);
  });

  const { request: generateReportRequest, loading: fetchingReport } = useApi({
    api: generateReport,
    pageError: true
  });

  const fetchCustomers = useCallback(
    (rs) => {
      getCustomnerAccountRequest(submitFilter({ ...rs }));
    },
    [getCustomnerAccountRequest, submitFilter]
  );

  // const changeStatusCb = useCallback(
  //   (customerId, driverName, status) => {
  //     statusModal.show({ status, customerId, driverName });
  //   },
  //   [statusModal]
  // );

  const onViewDetailsCb = useCallback(
    (customerAccountId) => {
      detailModal.show({ customerAccountId });
    },
    [detailModal]
  );

  const preparedDriversData = useMemo(() => {
    const { customerAccounts } = getCustomerResult;
    if (customerAccounts.length > 0) {
      const preparedData = customerAccounts.map((c) => {
        const map = new Map();
        const customerName = formatName(c.customer.firstName, c.customer.lastName);
        map.set('avatar', <Image src={DefaultProfile} className={styles.avatar} />);
        map.set('name', customerName);
        map.set('membershipId', c.customer.membershipId);
        map.set('customerAccountNumber', c.customerAccountNumber);
        map.set('balance', formatAmount(c.balance));
        map.set(
          'status',
          <Pill
            grass={c.status === CustomerStatus.Active}
            cheddar={c.status === CustomerStatus.Inactive}
            deepRed={c.status === CustomerStatus.Deactivated || c.status === CustomerStatus.Terminated}
          >
            {prettifyCustomerStatus(c.status)}
          </Pill>
        );

        let options = [];

        if (!c?.product?.name?.includes('Savings')) {
          options.push({
            content: 'View Details',
            onClick: () => {
              onViewDetailsCb(c.customerAccountId);
            }
          });
        }

        if (options && options.length > 0) {
          map.set('options', <PopOverMenu options={options} />);
        }

        return map;
      });
      return preparedData;
    }
    return [];
  }, [getCustomerResult, onViewDetailsCb]);

  const onStatusChangeCb = useCallback(
    (status) => {
      const { requestState } = modifyFilters({ status, page: 1 });
      fetchCustomers(requestState);
    },
    [fetchCustomers, modifyFilters]
  );

  const onSearchCb = useCallback(
    (searchKey) => {
      const { requestState } = modifyFilters({ searchKey, page: 1 });
      fetchCustomers(requestState);
    },
    [fetchCustomers, modifyFilters]
  );

  const onChangePageCb = useCallback(
    (page) => {
      const { requestState } = modifyFilters({ page });
      fetchCustomers(requestState);
    },
    [fetchCustomers, modifyFilters]
  );

  const onChangePageSizeCb = useCallback(
    (perPage) => {
      const { requestState } = modifyFilters({ perPage, page: 1 });
      fetchCustomers(requestState);
    },
    [fetchCustomers, modifyFilters]
  );

  const onDownloadFileCb = async () => {
    const { requestState } = modifyFilters({ reportType: 'customer-account', productId: 1 });

    delete requestState.perPage;
    delete requestState.page;

    const generateResult = await generateReportRequest(requestState);
    if (generateResult?.downloadUrl) {
      window.open(generateResult?.downloadUrl, '_blank');
    }
  };

  return (
    <div>
      <div>
        <Intro
          title={'Savings'}
          subtitle={locale.manageYourCustomers}
          // actionDisabled={}
        />
      </div>
      <div className={styles.filters}>
        <div>
          <CustomerFilter
            filterState={filterState}
            onStatusChange={onStatusChangeCb}
            onSearchChange={modifyFilter}
            onSearch={onSearchCb}
            onDownloadFile={onDownloadFileCb}
            fetchingReport={fetchingReport}
          />
        </div>
      </div>
      <div className={styles.table}>
        <DataTable
          loading={fetchingCustomer}
          page={filterState.page}
          pageSize={filterState.perPage}
          columns={savingsColumns}
          data={preparedDriversData}
          dataCount={getCustomerResult.count}
          onChangePage={onChangePageCb}
          onChangePageSize={onChangePageSizeCb}
        />
      </div>
    </div>
  );
};

export default SavingsModule;
